// Browse/search public startpages by tag, with a fork button.
import { useEffect, useState } from "react";
import { api } from "@/core/lib/api-client";
import type { Startpage } from "@/core/types";
import { GlassSurface } from "@/ui/GlassSurface";
import { useAuth } from "@/features/auth/AuthContext";

export function Discovery() {
  const { user } = useAuth();
  const [tags, setTags] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [pages, setPages] = useState<Startpage[]>([]);

  useEffect(() => {
    api.popularTags().then(setTags).catch(() => setTags([]));
  }, []);

  useEffect(() => {
    const load = activeTag ? api.byTag(activeTag) : api.publicFeed();
    load.then((p) => setPages(p.items)).catch(() => setPages([]));
  }, [activeTag]);

  async function fork(id: string) {
    if (!user) return alert("Sign in to fork a startpage.");
    await api.fork(id);
    alert("Forked! Find it on your dashboard.");
  }

  return (
    <div className="mx-auto max-w-6xl p-4">
      <h1 className="mb-4 text-2xl font-semibold">Discover startpages</h1>

      <div className="mb-6 flex flex-wrap gap-2">
        <Chip label="All" active={!activeTag} onClick={() => setActiveTag(null)} />
        {tags.map((t) => (
          <Chip key={t} label={`#${t}`} active={activeTag === t} onClick={() => setActiveTag(t)} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {pages.map((p) => (
          <GlassSurface key={p.id} className="p-4">
            <h2 className="text-lg font-medium">{p.title}</h2>
            <div className="mt-1 flex flex-wrap gap-1 text-xs opacity-70">
              {p.tags.map((t) => (
                <span key={t}>#{t}</span>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="opacity-60">{p.fork_count} forks</span>
              <button
                onClick={() => void fork(p.id)}
                className="rounded-full bg-white/15 px-3 py-1 hover:bg-white/25"
              >
                Fork
              </button>
            </div>
          </GlassSurface>
        ))}
        {pages.length === 0 && <p className="opacity-60">No public startpages yet.</p>}
      </div>
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-sm ${
        active ? "bg-white/30" : "bg-white/10 hover:bg-white/20"
      }`}
    >
      {label}
    </button>
  );
}
